/** Enums shared by the database, the API and the UI. */
/**
 * Addendum §E.4: `ai` is a first-class audit source. `sales_app` stays because
 * the log is insert-only and older rows may hold it (rule 2); nothing writes it.
 */
export const AUDIT_SOURCES = [
    'web', 'sales_app', 'crew_app', 'portal', 'api', 'system', 'integration', 'ai',
];
export const AUDIT_ACTIONS = [
    'insert', 'update', 'delete', 'restore', 'post', 'reverse', 'security_event',
];
/** Addendum §D.2: security events are recorded as events, never as field diffs. */
export const SECURITY_EVENTS = [
    'password_changed',
    'password_reset_requested',
    'password_reset_completed',
    'mfa_enabled',
    'mfa_disabled',
    'mfa_reset_by_admin',
    'recovery_codes_regenerated',
    'passkey_added',
    'passkey_removed',
    'pin_set',
    'pin_reset',
    'pin_locked',
    'session_created',
    'session_revoked',
    'sign_in_failed',
    'step_up_completed',
    'device_registered',
    'device_revoked',
    /*
     * Moving between environments (`docs/identity-and-sign-in.md` §2.2). Two
     * events because they land in two different logs: the origin records that a
     * visit was minted, the target that one was spent, and only both together
     * say a person crossed rather than that a link was clicked.
     */
    'switch_started',
    'switch_arrived',
    'account_linked',
    'account_unlinked',
    'impersonation_started',
    'impersonation_ended',
    'integration_connected',
    'integration_disconnected',
    'webhook_signature_invalid',
    'erasure_completed',
];
export const ORG_UNIT_KINDS = ['company', 'region', 'district', 'branch'];
export const DEVICE_KINDS = ['crew_ipad', 'sales_ipad', 'phone', 'browser'];
/** Addendum §E.5: retention rules apply to production and trial tenants only. */
export const TENANT_KINDS = ['production', 'trial', 'demo'];
export const TENANT_STATUSES = ['active', 'suspended', 'expired', 'cancelled'];
/*
 * Superseded by `MFA_KINDS` in `mfa.ts` — `docs/identity-and-sign-in.md` §4.
 *
 * This list was written before two-step was a list of methods, and it was
 * missing `email` and ordered arbitrarily, so it could not say which method a
 * sign-in should offer first. `MFA_KINDS` is the one list now, `MFA_ORDER`
 * says the order, and `MfaMethod` over there is a *row* — a method somebody
 * actually holds — rather than a word.
 *
 * Kept and re-exported so nothing that imported it breaks, deprecated so
 * nothing new reaches for it.
 *
 * @deprecated Use `MFA_KINDS` / `MfaKind` from `mfa.ts`.
 */
export const MFA_METHODS = ['totp', 'sms', 'passkey'];
/** Addendum §G.1: token kinds, each with its own expiry. */
/**
 * `choose` is the two-minute visit the one sign-in page hands out when an
 * address exists on both sides — `docs/rowted-admin-v2.md` §2.6. It is a
 * token kind rather than a session because nothing is signed in yet: the
 * password has passed and the person has still to say which door they meant.
 */
export const TOKEN_KINDS = ['magic_link', 'password_reset', 'invite', 'portal_invite', 'choose'];
export const TOKEN_TTL_MINUTES = {
    magic_link: 15,
    password_reset: 15,
    invite: 7 * 24 * 60,
    portal_invite: 7 * 24 * 60,
    choose: 2,
};
/**
 * Addendum §G.1: session idle timeouts by surface. `sales_app` is the 30-day
 * surface *Remember this device* signs in on — only a name, kept because the
 * sessions already stored carry it (there is no Sales app).
 */
export const SESSION_IDLE_MINUTES = {
    office: 12 * 60,
    sales_app: 30 * 24 * 60,
    crew_app: 14 * 60,
    portal: 60,
};
export const SESSION_ABSOLUTE_MAX_MINUTES = 30 * 24 * 60;
/** Addendum §G.4: actions that need a fresh credential within the last 10 minutes. */
export const STEP_UP_WINDOW_MINUTES = 10;
export const STEP_UP_ACTIONS = [
    'change_email',
    'change_password',
    'change_mfa',
    'view_audit_export',
    'change_integration_connection',
    'change_roles_permissions',
    'issue_refund_over_threshold',
    'issue_write_off_over_threshold',
    'start_impersonation',
];
/** Addendum §G.5: rate limits. */
export const RATE_LIMITS = {
    signIn: { limit: 10, windowMinutes: 15 },
    magicLink: { limit: 5, windowMinutes: 60 },
    pinAttempt: { limit: 3, windowMinutes: 1 },
};
/** Addendum §G.3: PIN lockout ladder. */
export const PIN_LOCKOUT = {
    lockAfterAttempts: 5,
    lockMinutes: 15,
    disableAfterAttempts: 10,
};
/**
 * The week a timesheet is measured against — plan §6.6's My hours screen.
 *
 * A standard week and an overtime threshold are the same number today, but
 * they are not the same idea: a company can run a 45-hour standard week and
 * still owe overtime after 40. Keeping them apart now means the day somebody
 * changes one, the other does not move with it.
 */
export const WORK_WEEK = {
    /** 0 = Sunday, 1 = Monday. Monday, because a crew's week starts Monday. */
    startsOn: 1,
    standardHours: 40,
    overtimeAfterHours: 40,
};
/** Addendum §H.1: idempotency key retention. */
export const IDEMPOTENCY_RETENTION_DAYS = 30;
/** Addendum §G.6: portal permission flags. */
export const PORTAL_PERMISSIONS = [
    'view_visits',
    'request_service',
    'approve_quotes',
    'view_invoices',
    'pay_invoices',
    'manage_payment_methods',
    'view_documents',
];
//# sourceMappingURL=enums.js.map